import { Alert, Input } from '@enterprise-ui/canvas-ui-react';
import React, { useState } from 'react';
import FileResizer from 'react-image-file-resizer';
import { dataURIToBlob } from '../utils/helpers';

export const PhotoUploader = ({
  files,
  onChange,
  minimum,
  maximum,
  isMaximumHit,
  isMinimumHit,
  fileSizeLimit,
  setMaxLimit,
  setMinLimit,
  deviceType,
}) => {
  const [duplicateWarning, setDuplicateWarning] = useState(false);
  const uploadPhotoError = isMaximumHit
    ? {
        type: 'error',
        heading: 'Error: Maximum limit exceeded',
        body: `Your photo selection exceeds the maximum limit of ${maximum}. Please remove a photo and try again.`,
      }
    : isMinimumHit
      ? {
          type: 'error',
          heading: 'Error: Missing photos',
          body: `Please upload at least ${minimum} photos of the vestibule.`,
        }
      : duplicateWarning
        ? {
            type: 'default',
            heading: 'Duplicate Photo(s) Found',
            body: 'Some uploaded photos already exist in this form. Please review your submission.',
          }
        : null;

  function resizeImage(file) {
    return new Promise((resolve, reject) => {
      try {
        FileResizer.imageFileResizer(
          file,
          1000, // maxWidth
          1000, // maxHeight
          'JPEG', // compressFormat
          100, // quality
          0, // rotation
          (resized) => {
            let resizedFile = dataURIToBlob(resized);
            const compressedFile = new File(
              [resizedFile],
              file.name, // Try to keep name after resizing to use file name logic
              {
                type: resizedFile.type || 'image/jpeg',
                lastModified: file.lastModified,
              },
            );
            resolve(compressedFile);
          },
          'base64', //'base64 required!!"
        );
      } catch (err) {
        reject(err);
      }
    });
  }

  async function handleUpdate(event) {
    try {
      const availableSpace = maximum - files?.length;
      const uploadedFiles = Array.from(event?.target?.files || []);
      const overMaxLimit = uploadedFiles.length - (maximum - files.length);
      const existingNames = new Set(files.map((f) => f.name));
      if (!uploadedFiles.length) return;
      //checking to see if files exist based on names
      const isFileDuplicate = uploadedFiles.some((file) =>
        existingNames.has(file.name),
      );
      //Shows warning for duplicates
      if (isFileDuplicate) {
        setDuplicateWarning(true);
      }
      if (availableSpace === 0 || uploadedFiles.length > availableSpace) {
        setMaxLimit(true);
        if (event?.target) event.target.value = '';
        return;
      } else {
        const processedFiles = await Promise.all(
          uploadedFiles.map((f) =>
            f.size > fileSizeLimit * 1000 * 1000 //Make it MB
              ? resizeImage(f)
              : Promise.resolve(f),
          ),
        );
        onChange([...files, ...processedFiles]);

        setMaxLimit(false);
        setMinLimit(false);
      }
      setMaxLimit(overMaxLimit > 0); //Triggered if over allowed limit
      if (event?.target) event.target.value = '';
    } catch (e) {
      console.log(e);
    }
  }

  const removeItem = (index) => {
    onChange(files.filter((o, i) => index !== i));
    setMaxLimit(false);
  };

  return (
    <div>
      {maximum > 1 &&
        files.map((photo, index) => {
          return (
            <div className="hc-clr-contrast-weak">
              <Alert
                key={index}
                type="success"
                heading="Success!"
                className="hc-clr-contrast-weak hc-bg-success hc-mb-normal hc-mt-none"
                onClose={() => {
                  removeItem(index);
                }}
              >
                {photo.name || 'Your photo'} has been uploaded!{' '}
              </Alert>
            </div>
          );
        })}
      {uploadPhotoError && (
        <Alert
          type={uploadPhotoError.type}
          heading={uploadPhotoError.heading}
          className="hc-mb-normal hc-mt-none"
          onClose={() => {
            setDuplicateWarning(false);
            setMaxLimit(false);
            setMinLimit(false);
          }}
        >
          <p className="hc-clr-contrast-weak">{uploadPhotoError.body}</p>
        </Alert>
      )}
      {maximum > 1 ? (
        <Input.Label>
          Please upload at least <strong>{minimum}</strong> of the required
          photos.
          <strong>The maximum number of photos allowed is {maximum}:</strong>
        </Input.Label>
      ) : deviceType ? (
        <Input.Label>
          Please upload a <strong>single</strong> photo showing both the
          Directional Arrow and MAC Address.
        </Input.Label>
      ) : (
        <Input.Label>
          Please upload a <strong>single</strong> photo showing the MAC Address.
        </Input.Label>
      )}
      <Input.DropArea
        data-testid="photo_area"
        id="photo_area"
        size="expanded"
        accept=".jpeg,.jpg,.png,.heic,.avif,.webp"
        multiple={true}
        dropText="Upload your photo here"
        onUpdate={handleUpdate}
        clickOnly
        fullwidth
      />
    </div>
  );
};
